import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiErrorText, apiFetch, type Case } from "../api.js";
import { useAuth } from "../auth.js";

/**
 * WHERE THE ATTORNEY SAYS HOW FAR THEIR OWN WORK HAS GOT — and, at the end,
 * gives the opinion itself.
 *
 * Before this, a professional holding a case could read it and upload documents
 * and nothing else — no act they could take moved anything, so every case they
 * were assigned sat at ASSIGNED permanently and their client's status page said
 * "The attorney does the search at the land registry" forever, in the present
 * tense, with no way for that to become true or false.
 *
 * TWO BUTTONS AND NO MORE, deliberately.
 *
 * These record a WORK STATE — a search began; it is finished being searched.
 * Neither records what the search found, and there is no third button here that
 * could. Only an enrolled attorney-at-law may state a conclusion about title,
 * the platform may never "generate, template, or auto-derive an
 * ownership/encumbrance conclusion" (COMPLIANCE Constraint 3), and whose
 * deliverable the resulting opinion even is — the attorney's, with the platform
 * as courier, or the platform's own product — is open decision D2, which
 * CLAUDE.md records as blocking who-signs and all packaging language.
 *
 * D2 was answered on 2026-08-16 — the opinion is the ATTORNEY'S and this
 * platform is the courier — so `SignOpinion` below now exists. What it does NOT
 * offer is a menu of outcomes to pick from: the conclusion is free text in the
 * attorney's own words, because a platform-defined vocabulary would make the
 * client read OUR summary of a legal opinion. Packaging and delivery remain
 * unbuilt (Q4, and there is no channel).
 */
export function CaseProgressActions({ caseRow }: { caseRow: Case }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const act = useMutation({
    mutationFn: async (path: "search-started" | "search-complete") =>
      apiFetch<Case>(`/cases/${caseRow.id}/${path}`, {
        token: await getToken(),
        method: "POST",
      }),
    // Both the professional's own list and any admin queue showing this case.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "cases"] }),
  });

  const canStart = caseRow.status === "ASSIGNED";
  // Accepts ASSIGNED too: an attorney who did the whole search before opening
  // the app should not have to record a start they have already finished.
  const canComplete = caseRow.status === "ASSIGNED" || caseRow.status === "SEARCH_IN_PROGRESS";

  if (caseRow.status === "UNDER_REVIEW") return <SignOpinion caseRow={caseRow} />;

  if (!canStart && !canComplete) {
    return (
      <p className="muted">
        <small>
          {caseRow.status === "OPINION_READY"
            ? "Your opinion is signed and your client can read it. It cannot be edited — a signed " +
              "opinion is superseded by a further one, never altered in place."
            : "There is nothing to record on a case at this stage."}
        </small>
      </p>
    );
  }

  return (
    <div className="actions">
      {canStart ? (
        <button type="button" disabled={act.isPending} onClick={() => act.mutate("search-started")}>
          I have started the registry search
        </button>
      ) : null}
      {canComplete ? (
        <button
          type="button"
          className="quiet"
          disabled={act.isPending}
          onClick={() => act.mutate("search-complete")}
        >
          The search is done — I am reviewing it
        </button>
      ) : null}
      {act.error ? (
        <p role="alert">{apiErrorText(act.error, "That did not record.")}</p>
      ) : null}
      <p className="muted">
        <small>
          These record where the work has got to, and your client sees it. Neither says anything
          about what you found.
        </small>
      </p>
    </div>
  );
}


/**
 * SIGNING. The one act on this platform that is a legal act.
 *
 * Two fields and no dropdown. `conclusion` is the attorney's own words, and
 * there is deliberately no menu of outcomes beside it — a platform-authored
 * vocabulary of {clear, encumbered, ...} would mean the attorney choosing from
 * OUR words and the client reading OUR characterisation of their legal opinion,
 * which COMPLIANCE Constraint 3 forbids in as many words.
 *
 * `periodSearched` is also free text, for the same reason in a smaller key: this
 * schema has no search-period field, the thirty-year chain exists only as
 * guidance prose, and a platform that computed "1996–2026" from a rule of thumb
 * would be stating a fact about a search it did not perform.
 */
function SignOpinion({ caseRow }: { caseRow: Case }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [conclusion, setConclusion] = useState("");
  const [periodSearched, setPeriodSearched] = useState("");
  const [confirming, setConfirming] = useState(false);

  const sign = useMutation({
    mutationFn: async () =>
      apiFetch(`/cases/${caseRow.id}/opinion`, {
        token: await getToken(),
        method: "POST",
        body: { conclusion, periodSearched },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "cases"] });
      void queryClient.invalidateQueries({ queryKey: ["opinion", caseRow.id] });
    },
  });

  const ready = conclusion.trim() !== "" && periodSearched.trim() !== "";

  return (
    <div className="sign-opinion">
      <h4>Your opinion</h4>
      <p className="muted">
        <small>
          This goes to your client as your own signed opinion — in your words, unedited, with
          your enrolment number against it. TitleVerify carries it and does not summarise it.
          It cannot be altered once signed.
        </small>
      </p>

      <label htmlFor={`period-${caseRow.id}`}>What period did the search cover?</label>
      <input
        id={`period-${caseRow.id}`}
        type="text"
        value={periodSearched}
        onChange={(e) => setPeriodSearched(e.target.value)}
        placeholder="e.g. 1978 to date, from the partition decree forward"
      />

      <label htmlFor={`conclusion-${caseRow.id}`}>Your opinion</label>
      <textarea
        id={`conclusion-${caseRow.id}`}
        rows={10}
        value={conclusion}
        onChange={(e) => setConclusion(e.target.value)}
      />

      {sign.error ? <p role="alert">{apiErrorText(sign.error, "That did not save.")}</p> : null}

      {/*
        Two steps, because signing is irreversible and this is a legal act. The
        confirmation states the two consequences that are actually irreversible
        rather than asking a generic "are you sure".
      */}
      {confirming ? (
        <>
          <p className="notice">
            <small>
              Signing sends this to your client as your opinion and cannot be undone. A signed
              opinion is superseded by issuing another, never edited.
            </small>
          </p>
          <div className="actions">
            <button type="button" disabled={sign.isPending} onClick={() => sign.mutate()}>
              {sign.isPending ? "Signing…" : "Sign and send to my client"}
            </button>
            <button type="button" className="quiet" onClick={() => setConfirming(false)}>
              Keep editing
            </button>
          </div>
        </>
      ) : (
        <div className="actions">
          <button type="button" disabled={!ready} onClick={() => setConfirming(true)}>
            Sign this opinion
          </button>
        </div>
      )}
    </div>
  );
}
